/**
 * Facility-analysis import.
 *
 * The external analysis pipeline groups the source DXF into semantic regions
 * (process bays, cleanrooms, equipment areas, …), each with a bounding box in
 * the *raw DXF coordinate space*. To overlay these on the canvas we project the
 * bboxes through the same transform the floor-plan geometry used (Y-flip,
 * translate to origin, uniform scale) — see ParsedFloorPlan.transform.
 */
import { FacilityRegion, FloorPlanTransform } from '../types';

/** One region as emitted by the analysis pipeline. */
export interface FacilityDocSection {
  id: string;
  name: string;
  type: string;
  confidence: number;
  bbox: {
    min_x: number;
    min_y: number;
    max_x: number;
    max_y: number;
    width?: number;
    height?: number;
    area?: number;
  };
  layer?: string;
  labels?: string[];
  equipment?: string[];
  equipment_count?: number;
  children?: unknown[];
  metadata?: Record<string, unknown>;
}

/** Top-level facility-analysis document. */
export interface FacilityDoc {
  facility?: {
    name?: string;
    total_regions?: number;
    sections?: FacilityDocSection[];
  };
  summary?: unknown;
  analysis?: unknown;
  adjacency_graph?: { region1: string; region2: string; type: string }[];
}

export interface ParsedFacility {
  name: string;
  regions: FacilityRegion[];
  /** Distinct region types present, in a stable display order. */
  types: string[];
  /** Combined extent of all regions in normalised canvas space. */
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
}

/** Preferred ordering for the legend / panel so common zones group together. */
const TYPE_ORDER = ['cleanroom', 'process_bay', 'equipment_area', 'unclassified'];

export function isFacilityDoc(doc: unknown): doc is FacilityDoc {
  return (
    !!doc &&
    typeof doc === 'object' &&
    'facility' in doc &&
    Array.isArray((doc as FacilityDoc).facility?.sections)
  );
}

/**
 * Projects every analysed section into canvas space using the floor-plan
 * transform. Regions with a degenerate / non-finite bbox are dropped.
 */
export function parseFacility(
  doc: FacilityDoc,
  transform: FloorPlanTransform,
  fallbackName = 'Facility analysis'
): ParsedFacility {
  const sections = doc.facility?.sections ?? [];
  const { rawMinX, rawMinY, invScale } = transform;

  const regions: FacilityRegion[] = [];
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  for (const s of sections) {
    const b = s.bbox;
    if (!b || !Number.isFinite(b.min_x) || !Number.isFinite(b.max_y)) continue;

    // X is unflipped; Y is flipped (canvas is Y-down) so max_y maps to the top.
    const x = (b.min_x - rawMinX) * invScale;
    const y = (-b.max_y - rawMinY) * invScale;
    const width = (b.max_x - b.min_x) * invScale;
    const height = (b.max_y - b.min_y) * invScale;
    if (!(width > 0) || !(height > 0)) continue;

    regions.push({
      id: s.id,
      name: s.name && s.name !== 'Unnamed' ? s.name : '',
      type: s.type || 'unclassified',
      confidence: typeof s.confidence === 'number' ? s.confidence : 0,
      x,
      y,
      width,
      height,
      layer: s.layer || '',
      equipmentCount: s.equipment_count ?? s.equipment?.length ?? 0,
      equipment: s.equipment ?? [],
    });

    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x + width > maxX) maxX = x + width;
    if (y + height > maxY) maxY = y + height;
  }

  const present = new Set(regions.map((r) => r.type));
  const ordered = TYPE_ORDER.filter((t) => present.has(t));
  const extras = Array.from(present).filter((t) => !TYPE_ORDER.includes(t)).sort();
  const types = [...ordered, ...extras];

  const bounds = Number.isFinite(minX)
    ? { minX, minY, maxX, maxY }
    : { minX: 0, minY: 0, maxX: 0, maxY: 0 };

  return {
    name: doc.facility?.name || fallbackName,
    regions,
    types,
    bounds,
  };
}

// ─── Type styling ─────────────────────────────────────────────────────────────

/** Base hue per region type. Fill is drawn translucent, border opaque. */
export const FACILITY_TYPE_COLORS: Record<string, string> = {
  cleanroom: '#8b5cf6', // violet
  process_bay: '#10b981', // emerald
  equipment_area: '#3b82f6', // blue
  unclassified: '#94a3b8', // slate
};

export function facilityTypeColor(type: string): string {
  return FACILITY_TYPE_COLORS[type] ?? '#f59e0b';
}

/** Friendly label for a region type. */
export function facilityTypeLabel(type: string): string {
  return type
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Returns the most specific (smallest-area) region whose rectangle contains the
 * canvas point (wx, wy) and passes the visibility/confidence filters, or null.
 */
export function pickRegion(
  wx: number,
  wy: number,
  regions: FacilityRegion[],
  visibleTypes: Record<string, boolean>,
  minConfidence: number
): FacilityRegion | null {
  let best: FacilityRegion | null = null;
  let bestArea = Infinity;
  for (let i = 0; i < regions.length; i++) {
    const r = regions[i];
    if (visibleTypes[r.type] === false) continue;
    if (r.confidence < minConfidence) continue;
    if (wx < r.x || wx > r.x + r.width || wy < r.y || wy > r.y + r.height) continue;
    const area = r.width * r.height;
    if (area < bestArea) {
      bestArea = area;
      best = r;
    }
  }
  return best;
}
